import {
  FeatureContext,
  getLink,
  useApi,
} from '@monash/portal-frontend-common';
import { Breadcrumb, fontStyles, LoadingIndicator } from '@monash/portal-react';
import { useContext, useEffect, useMemo } from 'react';
import c from './service-page.module.scss';
import ServiceForm from './components/service-form/ServiceForm';
import IneligibleCard from './components/ineligible-card/IneligibleCard';
import { SearchContextHoneycomb } from '@/components/providers/search-provider-honeycomb/SearchProviderHoneycomb';
import { isLinkType } from '../../utils';
import { LINK_TYPE } from '../../link-type';
import ImageSearchResultCardAdapter from '../../components/search-result-entity/adapters/ImageSearchResultCardAdapter';

const mockGetEligibility = async (id) => {
  console.debug('Mock get eligibility ', id);
  await new Promise((resolve) => setTimeout(resolve, 500));

  // Eligible for Peer Assisted Study Session (PASS)
  return id === '1'
    ? { isEligible: true }
    : {
        isEligible: false,
        reasons: [
          'You must be a Postgraduate student.',
          'You must have a minimum GPA of 3.0.',
        ],
      };
};

// This is currently unused, may be implemented in future Honeycomb phase
const ServicePage = ({ params, data }) => {
  const { featureFlags } = useContext(FeatureContext);
  const { id } = params;
  const { entity: entityProp } = data ?? {};

  const { searchResult, runSearch } = useContext(SearchContextHoneycomb);

  // Fetch link if not supplied via landing page
  const shouldFetchEntity = !entityProp;
  const { data: fetchedEntity, isLoading: isLoadingEntity } = useApi({
    requestFn: getLink,
    args: [id],
    initialLoading: shouldFetchEntity,
    skipInitialCall: !shouldFetchEntity,
  });

  const searchEntity = entityProp ?? fetchedEntity;
  const { name, description, links } = searchEntity ?? {};
  const externalLink = links?.[1]; // TODO: do i use the second link? how does this work

  const {
    data: eligibilityResp,
    isLoading: isLoadingEligibility,
    call: callGetEligibility,
  } = useApi({
    requestFn: mockGetEligibility,
    args: [id],
    skipInitialCall: true,
  });

  useEffect(() => {
    callGetEligibility(id);
  }, [id]);

  const { isEligible, reasons } = eligibilityResp ?? {};

  // Fetch search results if ineligible and if the results are not already stored
  const shouldRunSearch = !isEligible && !searchResult;
  const { isLoading: isLoadingSearch } = useApi({
    requestFn: runSearch,
    args: ['', false],
    initialLoading: shouldRunSearch,
    skipInitialCall: !shouldRunSearch,
  });

  const transformedSearchResult = useMemo(() => {
    if (!searchResult) return;
    let final = searchResult;

    final = searchResult
      .filter(
        (entity) =>
          isLinkType(LINK_TYPE.EXPERIENCE)(entity) ||
          isLinkType(LINK_TYPE.SERVICE)(entity)
      )
      .filter((entity) => entity.entityId !== id);

    const COUNT_SHOWN = 8;

    return final.slice(0, COUNT_SHOWN);
  }, [searchResult, id]);

  const isLoading = isLoadingEntity || isLoadingEligibility || isLoadingSearch;

  if (!featureFlags?.HONEYCOMB) return null;

  return (
    <div className={c.container}>
      {isLoading ? (
        <div className={c.loading}>
          <LoadingIndicator color="var(--canvas-text-color)" />
        </div>
      ) : (
        <>
          <Breadcrumb
            className={c.breadcrumb}
            items={[
              { label: 'Explore links', href: '/search' },
              { label: name },
            ]}
          />
          {isEligible ? (
            <ServiceForm
              name={name}
              description={description}
              externalLink={externalLink}
            />
          ) : (
            <div className={c.ineligibleContainer}>
              <IneligibleCard
                reasons={reasons}
                name={name}
                description={description}
                externalLink={externalLink}
              />
              <div className={c.explorationContainer}>
                <div className={fontStyles.heading}>Explore More</div>
                <div className={c.resultCardsContainer}>
                  {transformedSearchResult?.map((item) => (
                    <ImageSearchResultCardAdapter
                      entity={item.entityContent}
                      key={`entity-id-${item.entityId}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ServicePage;
